/**
 * OB Builder — shape the ladder in place.
 *
 * Split across the terminal rather than a panel below it, so the ladder stays the workspace:
 *
 *   <ObBuilderProvider>   holds the draft, debounced preview, and mutations
 *   <ObBuilderFairBar>    x / P / line, rungs and margin per side, Apply — inside the fair bar
 *   <ObBuilderSizeCell>   liquidity typed straight into the ladder's size column
 *
 * Only prices stay off-limits: they are always derived from (x, P) + margin by P_to_ob, which
 * is what keeps a cleared rung landing top-of-book on the next rung (INV-OB-16). Everything
 * the operator can touch here is orthogonal to that.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { postOrderBookBuilderPreview } from '../api';
import {
  useOrderBookBuilderApplyMutation,
  useOrderBookBuilderClearMutation,
} from '../api/hooks';

const PREVIEW_DEBOUNCE_MS = 250;
const ObBuilderCtx = createContext(null);

export const useObBuilder = () => useContext(ObBuilderCtx);

function num(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function fmtPct(p) {
  return p == null || !Number.isFinite(p) ? '—' : `${(p * 100).toFixed(2)}%`;
}

/**
 * Operator fair-probability input → a probability in (0, 1).
 *
 * Accepts what a trader actually types: ``62``, ``62%``, ``0.62``. Anything above 1 is read
 * as percent, which is the only reading that makes sense for a probability.
 */
function parseFairProb(raw) {
  if (raw == null || String(raw).trim() === '') return null;
  const t = String(raw).trim().replace('%', '');
  const n = Number(t);
  if (!Number.isFinite(n)) return NaN;
  const p = n > 1 ? n / 100 : n;
  return p > 0 && p < 1 ? p : NaN;
}

/** Posterior variance in logit space → readable ±pp at the 90% CI, for the P field's hint. */
function ciHintFromP(P, pFair) {
  const v = Number(P);
  const p = Number(pFair);
  if (!Number.isFinite(v) || v <= 0 || !Number.isFinite(p) || p <= 0 || p >= 1) return null;
  const x = Math.log(p / (1 - p));
  const sd = Math.sqrt(v);
  const lo = 1 / (1 + Math.exp(-(x - 1.64 * sd)));
  const hi = 1 / (1 + Math.exp(-(x + 1.64 * sd)));
  return `±${(((hi - lo) / 2) * 100).toFixed(1)}pp`;
}

/**
 * Edits → the price-keyed entries the API expects.
 *
 * Each edit carries the price of the row it was typed into, captured at edit time. Anchoring
 * to a price rather than a rung index is what stops a shape walking down the ladder when a
 * cleared rung collapses and everything renumbers (INV-OB-22). Capturing at edit time also
 * avoids a preview → overrides → preview loop.
 */
function toPriceEntries(edits) {
  const out = [];
  for (const [idx, e] of Object.entries(edits || {})) {
    if (!e || e.raw == null || e.raw === '') continue;
    const size = Number(e.raw);
    if (!Number.isFinite(size)) continue;
    if (e.p != null && Number.isFinite(Number(e.p))) {
      out.push({ p: Number(e.p), size });
    } else {
      out.push({ index: Number(idx), size });
    }
  }
  return out.length ? out : null;
}

/**
 * Builder state. Called by the terminal itself (not inside the provider) so the previewed
 * ladder is available while the rows are computed — the ladder *is* the preview.
 */
export function useObBuilderState({ marketKey, orderBook, quote, actedBy, active }) {
  const stored = orderBook?.builder_overrides || null;
  const snap = orderBook?.snapshot || null;

  /**
   * What each control reads when nothing is pinned: the values currently in force.
   *
   * Margins are recovered as (top-of-book implied − fair) on each side, which is exactly the
   * probability-point margin the operator would type — no separate API field needed.
   */
  const seeds = useMemo(() => {
    const mid = snap?.midpoint_prob;
    const yes0 = snap?.yes_levels?.[0]?.implied_prob;
    const no0 = snap?.no_levels?.[0]?.implied_prob;
    return {
      pFair: mid != null ? Number((mid * 100).toFixed(2)) : '',
      P: quote?.P != null ? Number(Number(quote.P).toFixed(4)) : '',
      line: quote?.line != null ? Number(quote.line) : '',
      nOver: snap?.yes_levels?.length ?? '',
      nUnder: snap?.no_levels?.length ?? '',
      mOver: mid != null && yes0 != null ? Number((yes0 - mid).toFixed(4)) : '',
      mUnder: mid != null && no0 != null ? Number((no0 - (1 - mid)).toFixed(4)) : '',
    };
  }, [snap, quote?.P, quote?.line]);

  const [pFair, setPFair] = useState('');
  const [P, setP] = useState('');
  const [line, setLine] = useState('');
  const [nOver, setNOver] = useState('');
  const [nUnder, setNUnder] = useState('');
  const [mOver, setMOver] = useState('');
  const [mUnder, setMUnder] = useState('');
  const seeded = useRef(false);
  // Sparse: { [levelIndex]: rawInputString }. Untouched rungs keep model size.
  const [liqOver, setLiqOver] = useState({});
  const [liqUnder, setLiqUnder] = useState({});

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const applyMut = useOrderBookBuilderApplyMutation(marketKey);
  const clearMut = useOrderBookBuilderClearMutation(marketKey);
  const seq = useRef(0);

  // Seed once per builder session, from the stored shape where one exists and the live book
  // otherwise. Re-seeding on every poll would fight the operator's typing.
  useEffect(() => {
    if (!active || seeded.current || seeds.pFair === '') return;
    seeded.current = true;
    setPFair(stored?.p_fair != null ? Number((stored.p_fair * 100).toFixed(2)) : seeds.pFair);
    setP(stored?.P ?? seeds.P);
    setLine(seeds.line);
    setNOver(stored?.level_count_over ?? seeds.nOver);
    setNUnder(stored?.level_count_under ?? seeds.nUnder);
    setMOver(stored?.margin_pp_over ?? seeds.mOver);
    setMUnder(stored?.margin_pp_under ?? seeds.mUnder);
  }, [active, seeds, stored]);

  // Leaving builder mode drops the draft so a stale edit cannot be applied later.
  useEffect(() => {
    if (!active) {
      seeded.current = false;
      setLiqOver({});
      setLiqUnder({});
      setPreview(null);
      setPreviewError(null);
    }
  }, [active]);

  /**
   * Only fields the operator actually moved off their seed are sent.
   *
   * This matters more than it looks: margin is normally a function of posterior uncertainty,
   * so shipping a seeded-but-untouched margin would silently convert a dynamic margin into a
   * value frozen at whatever it read when the builder was opened. "Looks unchanged" has to
   * mean "is unchanged".
   */
  const overrides = useMemo(() => {
    const o = {};
    const moved = (v, seed) =>
      v !== '' && v != null && Number.isFinite(Number(v)) && Number(v) !== Number(seed);

    if (moved(pFair, seeds.pFair)) {
      const pf = parseFairProb(pFair);
      if (pf != null && Number.isFinite(pf)) o.p_fair = pf;
    }
    if (moved(P, seeds.P)) o.P = num(P);
    if (moved(nOver, seeds.nOver)) o.level_count_over = Math.trunc(num(nOver));
    if (moved(nUnder, seeds.nUnder)) o.level_count_under = Math.trunc(num(nUnder));
    if (moved(mOver, seeds.mOver)) o.margin_pp_over = num(mOver);
    if (moved(mUnder, seeds.mUnder)) o.margin_pp_under = num(mUnder);
    const lo = toPriceEntries(liqOver);
    const lu = toPriceEntries(liqUnder);
    if (lo) o.liquidity_over = lo;
    if (lu) o.liquidity_under = lu;
    return o;
  }, [pFair, P, nOver, nUnder, mOver, mUnder, liqOver, liqUnder, seeds]);

  const dirty = Object.keys(overrides).length > 0;

  useEffect(() => {
    if (!active || !dirty) {
      setPreview(null);
      return undefined;
    }
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      setPreviewing(true);
      try {
        const res = await postOrderBookBuilderPreview(marketKey, overrides);
        if (seq.current === mine) {
          setPreview(res);
          setPreviewError(null);
        }
      } catch (e) {
        if (seq.current === mine) {
          setPreview(null);
          setPreviewError(e?.response?.data?.detail || e.message || 'preview failed');
        }
      } finally {
        if (seq.current === mine) setPreviewing(false);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [active, dirty, marketKey, overrides]);

  /** ``price`` is the implied prob of the row being typed into, captured now. */
  const setLiquidity = useCallback((side, index, raw, price) => {
    const set = side === 'over' ? setLiqOver : setLiqUnder;
    set((prev) => ({ ...prev, [index]: { raw, p: price ?? prev[index]?.p } }));
  }, []);

  const getLiquidity = useCallback(
    (side, index) => (side === 'over' ? liqOver : liqUnder)[index]?.raw,
    [liqOver, liqUnder]
  );

  const resetDraft = useCallback(() => {
    setPFair(seeds.pFair);
    setP(seeds.P);
    setLine(seeds.line);
    setNOver(seeds.nOver);
    setNUnder(seeds.nUnder);
    setMOver(seeds.mOver);
    setMUnder(seeds.mUnder);
    setLiqOver({});
    setLiqUnder({});
  }, [seeds]);

  /** Return one field to model-driven (un-pin it). */
  const resetField = useCallback(
    (key) => {
      const set = {
        pFair: setPFair, P: setP, line: setLine,
        nOver: setNOver, nUnder: setNUnder, mOver: setMOver, mUnder: setMUnder,
      }[key];
      if (set) set(seeds[key]);
    },
    [seeds]
  );

  const value = useMemo(
    () => ({
      active,
      marketKey,
      actedBy,
      snap,
      storedActive: Boolean(orderBook?.builder_active),
      fields: { pFair, P, line, nOver, nUnder, mOver, mUnder },
      seeds,
      resetField,
      setters: { setPFair, setP, setLine, setNOver, setNUnder, setMOver, setMUnder },
      liquidity: { get: getLiquidity, set: setLiquidity },
      overrides,
      dirty,
      preview,
      previewing,
      previewError,
      applyMut,
      clearMut,
      resetDraft,
    }),
    [
      active, marketKey, actedBy, snap, orderBook, pFair, P, line, nOver, nUnder, mOver, mUnder,
      getLiquidity, setLiquidity, overrides, dirty, preview, previewing, previewError, seeds, resetField,
      applyMut, clearMut, resetDraft,
    ]
  );

  return value;
}

/** Distributes the state from :func:`useObBuilderState` down to the editable size cells. */
export function ObBuilderProvider({ value, children }) {
  return <ObBuilderCtx.Provider value={value}>{children}</ObBuilderCtx.Provider>;
}

/**
 * One editable size cell — read-only rendering when the builder is off.
 *
 * ``current`` is the live remaining depth; the placeholder shows what the rung will hold if
 * left untouched, preferring the previewed value once a preview has landed.
 */
export function ObBuilderSizeCell({ side, index, current, fallback, level }) {
  const ctx = useObBuilder();
  if (!ctx?.active) {
    // Outside the builder, a rung the operator pinned still reads as operator-set — otherwise
    // a shaped book is indistinguishable from a model-driven one on the ladder.
    // ``operator_set`` = operator chose this size (the rung still trades and collapses
    // normally). ``operator_locked`` = pinned at zero: a price shown but not filled.
    if (level?.operator_set) {
      return (
        <span className="ob-builder-size-wrap">
          {fallback}
          <span className="ob-op-set" title="Operator-set liquidity (OB builder)">
            set
          </span>
        </span>
      );
    }
    return fallback;
  }
  const raw = ctx.liquidity.get(side, index);
  const previewLev =
    side === 'over' ? ctx.preview?.over?.levels?.[index] : ctx.preview?.under?.levels?.[index];
  const edited = raw != null && raw !== '';
  const placeholder = previewLev != null
    ? String(Math.round(previewLev.size))
    : String(Math.round(Number(current) || 0));
  return (
    <span className="ob-builder-size-wrap">
      <input
        type="number"
        className={`ob-builder-size-input${edited ? ' is-edited' : ''}`}
        value={raw ?? ''}
        step={10}
        min={1}
        placeholder={placeholder}
        aria-label={`${side} rung ${index} liquidity`}
        onChange={(e) => ctx.liquidity.set(side, index, e.target.value, level?.implied_prob)}
      />
      {edited ? (
        <button
          type="button"
          className="ob-builder-unpin"
          title={`Pinned — click to return to model (${placeholder})`}
          onClick={() => ctx.liquidity.set(side, index, '', level?.implied_prob)}
        >
          ×
        </button>
      ) : null}
      {edited && Number(raw) <= 0 ? (
        <span
          className="ob-builder-pin is-bad"
          title="Liquidity must be greater than 0 — a zero rung would publish a price with no size behind it. Use margin or rung count to move a price level."
        >
          &gt;0
        </span>
      ) : null}
    </span>
  );
}

/**
 * Seeded numeric field: shows the value in force, steps with the arrows, accepts a typed
 * value. Pinned (≠ seed) fields are marked and can be reset back to model-driven.
 *
 * The seed is display only. Only fields the operator actually moved are sent, so an untouched
 * margin keeps tracking uncertainty instead of being frozen at whatever it read when the
 * builder was opened.
 */
function NumField({ label, title, value, seed, onChange, onReset, step, min, max, suffix, hint }) {
  const pinned = value !== '' && value != null && Number(value) !== Number(seed);
  return (
    <label className={`ob-builder-num${pinned ? ' is-pinned' : ''}`} title={title}>
      <span>{label}</span>
      <input
        type="number"
        value={value ?? ''}
        step={step ?? 'any'}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
      />
      {suffix ? <em className="ob-builder-suffix">{suffix}</em> : null}
      {hint ? <em className="ob-builder-hint">{hint}</em> : null}
      {pinned ? (
        <button
          type="button"
          className="ob-builder-unpin"
          title={`Pinned — click to return to model (${fmtSeed(seed)})`}
          onClick={onReset}
        >
          ×
        </button>
      ) : null}
    </label>
  );
}

function fmtSeed(seed) {
  const n = Number(seed);
  return Number.isFinite(n) ? String(Number(n.toFixed(4))) : 'model';
}

function SideControls({ label, nKey, mKey, ctx, previewSide, liveTob }) {
  const { fields, setters } = ctx;
  const nVal = fields[nKey];
  const mVal = fields[mKey];
  const setN = setters[`set${nKey[0].toUpperCase()}${nKey.slice(1)}`];
  const setM = setters[`set${mKey[0].toUpperCase()}${mKey.slice(1)}`];
  const nextTob = previewSide?.levels?.[0]?.implied_prob;
  const moved = nextTob != null && liveTob != null && Math.abs(nextTob - liveTob) > 1e-9;
  return (
    <div className="ob-builder-side-ctl">
      <span className="ob-builder-side-label">{label}</span>
      <NumField
        label="rungs"
        title="Rungs on this side. 0 removes the side entirely."
        value={nVal}
        seed={ctx.seeds[nKey]}
        onChange={setN}
        onReset={() => ctx.resetField(nKey)}
        step={1}
        min={0}
        max={50}
      />
      <NumField
        label="margin"
        title="Flat probability margin in points; clamped to the market's [floor, ceiling]"
        value={mVal}
        seed={ctx.seeds[mKey]}
        onChange={setM}
        onReset={() => ctx.resetField(mKey)}
        step={0.005}
        min={0}
        max={0.5}
      />
      <span className="ob-builder-tob" title="Top of book on this side">
        {fmtPct(liveTob)}
        {moved ? <> → <strong>{fmtPct(nextTob)}</strong></> : null}
        {previewSide ? <em> · {previewSide.n_levels}r</em> : null}
      </span>
    </div>
  );
}

/** The control strip that lives inside the "Our fair" bar. */
export function ObBuilderFairBar() {
  const ctx = useObBuilder();
  if (!ctx?.active) return null;
  const {
    fields, setters, preview, previewing, previewError, dirty,
    applyMut, clearMut, overrides, actedBy, storedActive, resetDraft, snap,
  } = ctx;

  const pf = parseFairProb(fields.pFair);
  const pfBad = fields.pFair !== '' && fields.pFair != null && !Number.isFinite(pf);
  /**
   * Reseeding is decided by what actually **moved**, not by what is populated. Every field is
   * seeded with the value in force, so "is the box filled in" is always true and would mark
   * every apply — including a pure liquidity edit — as a posterior reseed.
   */
  const wantsReseed = overrides.p_fair != null || overrides.P != null;
  const reseedReady = !wantsReseed || num(fields.line) != null;
  const ciHint = ciHintFromP(num(fields.P), pf ?? snap?.midpoint_prob);
  const errors = [
    ...(pfBad ? ['fair must be a probability between 0 and 100% — e.g. 62, 62% or 0.62'] : []),
    ...(previewError ? [previewError] : []),
    ...((preview?.errors) || []),
  ];
  const warnings = preview?.warnings || [];
  const canApply =
    dirty && reseedReady && !pfBad && !errors.length && preview?.buildable && !applyMut.isPending;

  const liveOverTob = snap?.yes_levels?.[0]?.implied_prob;
  const liveUnderTob = snap?.no_levels?.[0]?.implied_prob;

  return (
    <div className="ob-builder-bar">
      <div className="ob-builder-bar-row">
        <SideControls
          label="OVER"
          nKey="nOver"
          mKey="mOver"
          ctx={ctx}
          previewSide={preview?.over}
          liveTob={liveOverTob}
        />
        <SideControls
          label="UNDER"
          nKey="nUnder"
          mKey="mUnder"
          ctx={ctx}
          previewSide={preview?.under}
          liveTob={liveUnderTob}
        />
      </div>

      <div className="ob-builder-bar-row">
        <div className="ob-builder-side-ctl ob-builder-posterior">
          <span className="ob-builder-side-label">POSTERIOR</span>
          <NumField
            label="fair"
            title="Fair probability of the over, in percent. Applying reseeds the posterior."
            value={fields.pFair}
            seed={ctx.seeds.pFair}
            onChange={setters.setPFair}
            onReset={() => ctx.resetField('pFair')}
            step={0.25}
            min={0.01}
            max={99.99}
            suffix="%"
          />
          <NumField
            label="P"
            title="Posterior variance in log-odds space. The hint shows the 90% CI it implies."
            value={fields.P}
            seed={ctx.seeds.P}
            onChange={setters.setP}
            onReset={() => ctx.resetField('P')}
            step={0.001}
            min={0.0001}
            hint={ciHint}
          />
          <NumField
            label="line"
            title="Baseline line — required when reseeding the posterior"
            value={fields.line}
            seed={ctx.seeds.line}
            onChange={setters.setLine}
            onReset={() => ctx.resetField('line')}
            step={0.5}
          />
        </div>

        <div className="ob-builder-bar-actions">
          {previewing ? <span className="ob-builder-spin">preview…</span> : null}
          <button
            type="button"
            className="ob-builder-apply"
            disabled={!canApply}
            title={
              wantsReseed
                ? 'Reseeds the posterior, then rebuilds the ladder with this shape'
                : 'Store this shape and rebuild the ladder. The posterior is not touched.'
            }
            onClick={() =>
              applyMut.mutate({
                overrides,
                acted_by: actedBy,
                reseed_posterior: wantsReseed,
                line: num(fields.line),
              })
            }
          >
            {applyMut.isPending ? 'Applying…' : 'Apply'}
          </button>
          {dirty ? (
            <button type="button" className="ob-builder-ghost" onClick={resetDraft}>
              Reset
            </button>
          ) : null}
          {storedActive ? (
            <button
              type="button"
              className="ob-builder-ghost"
              disabled={clearMut.isPending}
              title="Drop the stored shape and rebuild from the model"
              onClick={() => clearMut.mutate(actedBy)}
            >
              {clearMut.isPending ? 'Reverting…' : 'Revert to model'}
            </button>
          ) : null}
        </div>
      </div>

      {wantsReseed ? (
        <div className="ob-builder-bar-note">
          Fair/P changed — applying will reseed the posterior and lock the baseline.
        </div>
      ) : null}
      {errors.length ? (
        <div className="ob-builder-bar-msg is-error">{errors.join(' · ')}</div>
      ) : null}
      {warnings.length ? (
        <div className="ob-builder-bar-msg is-warn">{warnings.join(' · ')}</div>
      ) : null}
      {applyMut.isError ? (
        <div className="ob-builder-bar-msg is-error">
          {applyMut.error?.response?.data?.detail || applyMut.error?.message || 'apply failed'}
        </div>
      ) : null}
    </div>
  );
}
